import { KeycloakTokenParsed } from "keycloak-js";

// Keycloak realm roles granted to the `sync_admin` group (backend:
// com.vngrs.syncrosale.role.AdminRole). Holding any of these shows the admin
// group in the sidebar.
export const ADMIN_ROLES = {
  LIST_SELLERS: "LIST_SELLERS",
  DEACTIVATE_SELLERS: "DEACTIVATE_SELLERS",
  VIEW_FEATURE_FLAGS: "VIEW_FEATURE_FLAGS",
  MANAGE_FEATURE_FLAGS: "MANAGE_FEATURE_FLAGS",
  VIEW_WAREHOUSES: "VIEW_WAREHOUSES",
  MANAGE_WAREHOUSES: "MANAGE_WAREHOUSES",
  VIEW_SURCHARGES: "VIEW_SURCHARGES",
  MANAGE_SURCHARGES: "MANAGE_SURCHARGES",
  TRIGGER_PRODUCT_SYNC: "TRIGGER_PRODUCT_SYNC",
  MANAGE_ACTUATOR: "MANAGE_ACTUATOR",
  MANAGE_SELLER_SUSPENSION: "MANAGE_SELLER_SUSPENSION",
  VIEW_LISTING_SYNC: "VIEW_LISTING_SYNC",
} as const;

export type AdminAccess = {
  isAdmin: boolean;
  canViewFeatureFlags: boolean;
  canManageFeatureFlags: boolean;
  canViewWarehouses: boolean;
  canManageWarehouses: boolean;
  canViewSurcharges: boolean;
  canManageSurcharges: boolean;
  canTriggerProductSync: boolean;
  canManageActuator: boolean;
  canManageSellerSuspension: boolean;
  canListSellers: boolean;
  canViewListingSync: boolean;
};

export type AdminPageKey =
  | "feature-flags"
  | "warehouses"
  | "surcharges"
  | "product-sync"
  | "sellers"
  | "listing-sync"
  | "system";

// The sidebar sections, as the backend splits the admin API: what is done on
// behalf of a seller (/admin/...), the platform's own settings
// (/feature-flags, /warehouses) and the infrastructure (the actuator).
export type AdminMenuGroup =
  | "seller-operations"
  | "platform-settings"
  | "infrastructure";

type AdminPage = {
  path: string;
  // The realm role the page needs; shown to admins who lack it.
  role: string;
  allowed: (_access: AdminAccess) => boolean;
  // Sub-pages are opened from their parent page, not from the sidebar.
  inMenu: boolean;
  // its sidebar section
  group: AdminMenuGroup;
};

// The role behind every admin page, in sidebar order. The sidebar and the
// route guard read this one table; the backend enforces the same roles.
export const ADMIN_PAGES: Record<AdminPageKey, AdminPage> = {
  sellers: {
    path: "/admin/sellers",
    role: ADMIN_ROLES.LIST_SELLERS,
    allowed: (access) => access.canListSellers,
    inMenu: true,
    group: "seller-operations",
  },
  "product-sync": {
    path: "/admin/product-sync",
    role: ADMIN_ROLES.TRIGGER_PRODUCT_SYNC,
    allowed: (access) => access.canTriggerProductSync,
    inMenu: true,
    group: "seller-operations",
  },
  "listing-sync": {
    path: "/admin/listing-sync",
    role: ADMIN_ROLES.VIEW_LISTING_SYNC,
    allowed: (access) => access.canViewListingSync,
    inMenu: true,
    group: "seller-operations",
  },
  "feature-flags": {
    path: "/admin/feature-flags",
    role: ADMIN_ROLES.VIEW_FEATURE_FLAGS,
    allowed: (access) => access.canViewFeatureFlags,
    inMenu: true,
    group: "platform-settings",
  },
  warehouses: {
    path: "/admin/warehouses",
    role: ADMIN_ROLES.VIEW_WAREHOUSES,
    allowed: (access) => access.canViewWarehouses,
    inMenu: true,
    group: "platform-settings",
  },
  surcharges: {
    path: "/admin/warehouses",
    role: ADMIN_ROLES.VIEW_SURCHARGES,
    allowed: (access) => access.canViewSurcharges,
    inMenu: false,
    group: "platform-settings",
  },
  system: {
    path: "/admin/system",
    role: ADMIN_ROLES.MANAGE_ACTUATOR,
    allowed: (access) => access.canManageActuator,
    inMenu: true,
    group: "infrastructure",
  },
};

// The sidebar pages the user may open, in sidebar order.
export const allowedAdminMenuPages = (access: AdminAccess): AdminPageKey[] =>
  (Object.keys(ADMIN_PAGES) as AdminPageKey[]).filter(
    (key) => ADMIN_PAGES[key].inMenu && ADMIN_PAGES[key].allowed(access),
  );

// First admin page the user may open; where an admin without a store lands.
export const getAdminHome = (access: AdminAccess): string => {
  if (access.canViewFeatureFlags) return "/admin/feature-flags";
  if (access.canViewWarehouses) return "/admin/warehouses";
  if (access.canTriggerProductSync) return "/admin/product-sync";
  if (access.canListSellers) return "/admin/sellers";
  if (access.canViewListingSync) return "/admin/listing-sync";
  if (access.canManageActuator) return "/admin/system";
  return "/admin/feature-flags";
};

export const getAdminAccess = (
  tokenParsed: KeycloakTokenParsed | undefined,
): AdminAccess => {
  const roles = tokenParsed?.realm_access?.roles ?? [];
  const adminRoles: string[] = Object.values(ADMIN_ROLES);

  return {
    isAdmin: roles.some((role) => adminRoles.includes(role)),
    canViewFeatureFlags: roles.includes(ADMIN_ROLES.VIEW_FEATURE_FLAGS),
    canManageFeatureFlags: roles.includes(ADMIN_ROLES.MANAGE_FEATURE_FLAGS),
    canViewWarehouses: roles.includes(ADMIN_ROLES.VIEW_WAREHOUSES),
    canManageWarehouses: roles.includes(ADMIN_ROLES.MANAGE_WAREHOUSES),
    canViewSurcharges: roles.includes(ADMIN_ROLES.VIEW_SURCHARGES),
    canManageSurcharges: roles.includes(ADMIN_ROLES.MANAGE_SURCHARGES),
    canTriggerProductSync: roles.includes(ADMIN_ROLES.TRIGGER_PRODUCT_SYNC),
    canManageActuator: roles.includes(ADMIN_ROLES.MANAGE_ACTUATOR),
    canListSellers: roles.includes(ADMIN_ROLES.LIST_SELLERS),
    canViewListingSync: roles.includes(ADMIN_ROLES.VIEW_LISTING_SYNC),
    canManageSellerSuspension: roles.includes(
      ADMIN_ROLES.MANAGE_SELLER_SUSPENSION,
    ),
  };
};
